import { type BasicScheduleTimeData, EmptyWeek, type WeekdayData } from "@qendo/schedule";
import type { DateArg } from "date-fns";
import type { NotificationContentInput, NotificationTriggerInput } from "expo-notifications";
import type { Href } from "expo-router";
import type { QendoIconSet } from "~/components/QendoIcon";
import type { NotificationSettings } from "~/entities/notification-settings";

export enum NotificationFrequency {
    flare = "f",
    daily = "d",
    specific_days = "s",
    interval = "i",
}
export type NotificationFrequencyItem<T extends NotificationFrequency = NotificationFrequency> = {
    value: T;
    label: string;
    subtitle?: string;
};
export type NotificationFrequencyItemRecord<T extends NotificationFrequency> = Record<T, NotificationFrequencyItem<T>>;
export type NotificationFrequencyItemEntry<T extends NotificationFrequency = NotificationFrequency> = [
    T,
    NotificationFrequencyItem<T>,
];

export const NotificationFrequencyMap: NotificationFrequencyItemRecord<NotificationFrequency> = {
    [NotificationFrequency.flare]: { value: NotificationFrequency.flare, label: "The day after I log a flare" },
    [NotificationFrequency.daily]: { value: NotificationFrequency.daily, label: "Every day" },
    [NotificationFrequency.specific_days]: {
        value: NotificationFrequency.specific_days,
        label: "On specific days of the week",
        subtitle: "“On Monday, Wednesday & Thursday”",
    },
    [NotificationFrequency.interval]: {
        value: NotificationFrequency.interval,
        label: "On an interval",
        subtitle: "“Once every 3 days”",
    },
};
type BaseNotificationSchedule<F extends NotificationFrequency> = {
    frequency: F;
    times: BasicScheduleTimeData[];
};
export type FlareNotificationSchedule = BaseNotificationSchedule<NotificationFrequency.flare>;
export type DailyNotificationSchedule = BaseNotificationSchedule<NotificationFrequency.daily>;
export type WeeklyNotificationSchedule = {
    days: WeekdayData;
} & BaseNotificationSchedule<NotificationFrequency.specific_days>;
export type IntervalNotificationSchedule = {
    interval: number;
} & BaseNotificationSchedule<NotificationFrequency.interval>;

export enum NotificationTriggerGroup {
    cycle = "cycle",
    pain = "pain",
    calendar = "calendar",
    flare = "f",
    daily = "d",
    specific_days = "s",
    interval = "i",
}

export enum CycleKey {
    prediction = "period_prediction",
    start = "period_start",
    ovulation = "ovulation_prediction",
}

export type NotificationSchedule<F extends NotificationFrequency = NotificationFrequency> =
    BaseNotificationSchedule<F> &
        (
            | FlareNotificationSchedule
            | DailyNotificationSchedule
            | WeeklyNotificationSchedule
            | IntervalNotificationSchedule
        );

export type NotificationData = {
    identifier: string;
    title?: string | null;
    subtitle?: string | null;
    body?: string | null;
    data?: Record<string, unknown>;
    date: DateArg<Date>;
    href?: Href;
    seen: boolean;
    icon?: QendoIconSet;
};

type DefaultNotifications = Partial<NotificationSettings> & {
    affirmations: DailyNotificationSchedule;
    breath: IntervalNotificationSchedule;
    log_pain: FlareNotificationSchedule;
    log_symptom: DailyNotificationSchedule;
    drink_water: DailyNotificationSchedule;
    pelvic_exercises: DailyNotificationSchedule;
    pelvic_floor: IntervalNotificationSchedule;
    move_your_body: DailyNotificationSchedule;
    rest: WeeklyNotificationSchedule;
};
export const DefaultNotificationSettings: DefaultNotifications = {
    period_prediction: true,
    ovulation_prediction: true,
    pain: true,
    affirmations: {
        frequency: NotificationFrequency.daily,
        times: [{ time: "10:30:00" }],
    },
    breath: {
        frequency: NotificationFrequency.interval,
        interval: 7,
        times: [{ time: "09:00:00" }],
    },
    log_pain: {
        frequency: NotificationFrequency.flare,
        times: [{ time: "09:30:00" }],
    },
    log_symptom: {
        frequency: NotificationFrequency.daily,
        times: [{ time: "20:30:00" }],
    },
    drink_water: {
        frequency: NotificationFrequency.daily,
        times: [{ time: "08:30:00" }, { time: "12:30:00" }, { time: "17:30:00" }],
    },
    pelvic_exercises: {
        frequency: NotificationFrequency.daily,
        times: [{ time: "08:30:00" }, { time: "12:30:00" }, { time: "17:30:00" }],
    },
    pelvic_floor: {
        frequency: NotificationFrequency.interval,
        interval: 7,
        times: [{ time: "12:00:00" }],
    },
    move_your_body: {
        frequency: NotificationFrequency.daily,
        times: [{ time: "14:30:00" }],
    },
    rest: {
        frequency: NotificationFrequency.specific_days,
        days: { ...EmptyWeek, sunday: true },
        times: [{ time: "10:00:00" }],
    },
};

type ScheduleNotificationContentInput = {
    data?: {
        icon?: QendoIconSet;
        href?: Href;
    };
} & NotificationContentInput;

export type ScheduleNotificationRequest = {
    identifier?: string;
    content: ScheduleNotificationContentInput;
    trigger?: NotificationTriggerInput;
};

export const NotificationOptions = {
    pain: [
        "You’ve been dealing with a lot of pain. Maybe it’s time to chat with your GP or physio?",
        "Oof, I see you’ve logged a lot of pain lately. Maybe your GP needs a heads-up. ",
        "Hey, pain’s been rough lately. Think about checking in with your physio or doctor— or track if you’ve done something different to aggravate it?",
        "I know the pain can be overwhelming, but remember, you’re not weak for feeling it. You’re incredibly strong for enduring it.",
        "Your pain is your story, but it doesn’t own you. Let’s show this disease who’s boss today.",
        "Bad days are real, but so are better ones. Hang in there, warrior—you’ve got this.",
        "It’s okay to scream, cry, or rest today. Do whatever you need to do to take care of you. No guilt allowed.",
        "If today feels heavy, give yourself permission to let go of expectations. You’re doing your best, and that’s enough.",
        "When you feel like the world is moving too fast, it’s okay to hit pause. Your health comes first.",
        "Dealing with endo is a full-time job, so don’t feel bad about taking a day off from everything else",
        "You’re not being ‘dramatic.’ Your pain is real, and you deserve the same care you show others.",
        "Chronic illness is a rollercoaster, but we ride it together. Lean on your support system today if you need to.",
    ],
    affirmations: [
        "Don’t forget to show yourself some love today.",
        "You’re doing amazing, even if today feels tough. Take a deep breath, you’ve got this!",
        "Sending you a little reminder to be kind to yourself today. You’re stronger than you know!",
        "You are more than your pain. You are strength, resilience, and courage wrapped up in one amazing human.",
        "Your pain is real. Your struggles are valid.",
        "Remember: You don’t have to do everything today. Rest is productive too. It’s okay to take it easy.",
        "Some days, surviving is more than enough. And today, I’m so proud of you for doing just that.",
        "It’s okay to pause and listen to your body. It’s fighting hard for you—let it rest when it needs to.",
        "Sending you a gentle reminder: you are not alone in this fight. We’re all in this together, supporting each other.",
        "You’ve handled everything life has thrown your way so far, and you’ll handle this too. Endo doesn’t define you",
        "Your body may feel like it’s against you, but your spirit is fierce. Let’s get through this, one moment at a time.",
        "Some days, self-care means showing up for yourself in the smallest of ways. You are doing enough, just by being.",
        "You’re doing great, even on the tough days. Remember how strong you are — you’ve got this!",
        "Hey you, take a moment to remember just how amazing you are. You’re a warrior, no matter what today brings.",
        "Quick reminder: You are enough, and you’re stronger than you think.",
    ],
    period_prediction: [
        "Heads up! Your period might be here in 3-5 days.",
        "You know the drill—your period could arrive in a few days. Time to stock up and find those heat packs.",
        "Looks like your period is around the corner. Get yourself prepped.",
    ],
    period_start: [
        "Hey friend, today could be the day your period starts. We’re ready when you are — track it when you’re ready!",
        "Looks like your period might start today. How are you feeling?",
        "Tears in the last few days? Period day might be today.",
    ],
    ovulation_prediction: [
        "Ovulation’s coming up — is the heatpack handy?",
        "Your body’s gearing up for ovulation. Take a note which side you're feeling symptoms and track",
        "Ovulation time soon! Are you ovulating on the same side or different side this month?",
    ],
    self_care: [
        "You deserve some ‘me time’ today. Go ahead and treat yourself like the queen you are!",
        "Just a little reminder: take a moment for yourself today. You deserve every bit of it!",
        '"Hey, don’t forget to show yourself some love today. You’re worth it!"',
        "You are powerful, even when you’re resting. Healing is happening, even on the slow days.",
        "I see your fight, and it inspires me every day. Keep going—you are more resilient than you know.",
        "No one knows what it’s like to be you, fighting this fight every day. But I see you, and you’re doing an amazing job.",
        "The world needs your voice. Your experience with endo gives you a strength and wisdom no one else has.",
        "It’s okay to be both a mess and a masterpiece. You’re beautiful, inside and out, even through the pain.",
        "Rest today, rise tomorrow. You’ve got this—whether you’re moving mountains or simply getting through the day.",
    ],
    drink_water: [
        "Hydrate like a boss! Grab a glass of water and keep things flowing smoothly.",
        "Friendly hydration nudge: It’s time to sip some water and keep that glow going!",
        "Water break! Keep sipping, you fabulous human.",
    ],
    pelvic_floor: [
        "Quick check! Are you holding tension in your pelvic floor? Take a breath and let it go. You got this!",
        "Friendly reminder: Relax that pelvic floor! Unclench and breathe — you’ll thank me later.",
        "Pelvic floor check: Time to take a deep breath, friend. Let’s release that tension.",
    ],
    pelvic_exercises: [
        "Hydration check! Also, how are those pelvic floor exercises coming along? Let’s do this!",
        "Your body will thank you for some water and pelvic floor exercises today.",
        "Reminder to drink that water and rock those pelvic floor exercises — your pelvis will thank you.",
    ],
    symptoms: [
        "Let’s take a peek at your symptom trends. Curious to see how things stack up? We’re on it!",
        "Wanna see how your symptoms are adding up? Check your reports — let’s make sense of it together.",
        "Ever wonder how your symptoms compare? Check your reports and let’s find those patterns.",
    ],
    appointment: [
        "Just a heads-up: your appointment’s coming up soon. Let’s get you ready to rock it!",
        "You’ve got an appointment soon! Let’s get you prepped and feeling confident.",
        "Your appointment is just around the corner. Let’s crush it together—preparation is key!",
    ],
    breath: [
        "Hey there, take a moment to breathe deeply.",
        "Breath check! Slow down, take a deep breath, and let’s get some calm vibes flowing.",
        "Quick break: time for a few deep breaths. You deserve to feel at ease, even if just for a moment!",
    ],
    rest: [
        "You’ve been going non-stop! Time to take a break, relax, and recharge. You’ve earned it.",
        "Reminder: You deserve a little R&R today. Rest is just as important as the hustle!",
        "Friendly nudge: It’s break time! Kick your feet up and recharge—you’ve totally earned this.",
    ],
    move_your_body: [
        "How about a quick stretch or a walk? Your body will thank you for a little movement today.",
        "Let’s get moving, friend! A quick stretch, walk, or dance break will work wonders.",
        "Friendly reminder: Time for some movement. Stretch, shake it out, or dance it off—your body will love you for it!",
    ],
    log_symptom: [
        "Hey! Time to log those symptoms and check in.",
        "Hey you! Have a sec? Let’s track those symptoms so we can tackle this.",
        "Quick check-in, friend! Let’s log how you’re feeling today. Future you will thank us!",
    ],
    medication: [
        "Your meds and supplements are calling, and I’m here to make sure you don’t ghost them.",
        "Psst… friendly reminder to take your meds or supplements!",
        "Time for your meds or supplements, friend!",
    ],
    log_pain: [
        "Hey! Time to log your pain and check in.",
        "Hey you! Have a sec? Let’s track your pain so we can tackle this.",
        "Quick check-in, friend! Let’s log how you’re feeling today. Future you will thank us!",
    ],
};

export type NotificationTarget = string & keyof typeof NotificationOptions;

export const NotificationTitles = {
    pain: "Pain Level Check-In",
    period_prediction: "Period prediction",
    period_start: "Period due",
    ovulation_prediction: "Ovulation prediction",
    drink_water: "Hydration reminder",
    symptoms: "Symptom reminder",
    appointment: "Upcoming appointment",
    log_symptom: "Symptom reminder",
    log_pain: "Pain reminder",
    medication: "Medication reminder",
};
export type TitledNotification = string & keyof typeof NotificationTitles;

export type FlareTarget = NotificationTarget &
    ("drink_water" | "pelvic_exercises" | "pelvic_floor" | "move_your_body" | "rest" | "log_pain" | "log_symptom");

export type FlareNotificationTarget = {
    key: FlareTarget;
    times: BasicScheduleTimeData[];
};

export type ScheduleNotificationTarget = {
    key: NotificationTarget;
    frequency: NotificationTriggerGroup;
    times: BasicScheduleTimeData[];
    days: WeekdayData;
    interval: number;
};
